import React, { useState } from 'react';
import { ArrowLeft, Save, X, Shield, Key, Check } from 'lucide-react';

const RoleForm = ({ onBack, roleData, mode }) => {
  const isEditMode = mode === 'edit';
  
  const [formData, setFormData] = useState({
    name: roleData?.name || '',
    description: roleData?.description || '',
    permissions: roleData?.permissions || []
  });

  const [errors, setErrors] = useState({});

  // Available permissions grouped by resource
  const availablePermissions = {
    'User Management': [
      { id: 'user.create', name: 'Create User', description: 'Create new users' },
      { id: 'user.read', name: 'View User', description: 'View user information' },
      { id: 'user.update', name: 'Update User', description: 'Update user information' },
      { id: 'user.delete', name: 'Delete User', description: 'Delete users' }
    ],
    'Role Management': [
      { id: 'role.create', name: 'Create Role', description: 'Create new roles' },
      { id: 'role.read', name: 'View Role', description: 'View role information' },
      { id: 'role.update', name: 'Update Role', description: 'Update role information' },
      { id: 'role.delete', name: 'Delete Role', description: 'Delete roles' }
    ],
    'Permission Management': [
      { id: 'permission.create', name: 'Create Permission', description: 'Create new permissions' },
      { id: 'permission.read', name: 'View Permission', description: 'View permission information' },
      { id: 'permission.update', name: 'Update Permission', description: 'Update permission information' },
      { id: 'permission.delete', name: 'Delete Permission', description: 'Delete permissions' }
    ],
    'Project Management': [
      { id: 'project.create', name: 'Create Project', description: 'Create new projects' },
      { id: 'project.read', name: 'View Project', description: 'View project information' },
      { id: 'project.update', name: 'Update Project', description: 'Update project information' },
      { id: 'project.delete', name: 'Delete Project', description: 'Delete projects' }
    ],
    'Budget Management': [
      { id: 'budget.create', name: 'Create Budget', description: 'Create budget entries' },
      { id: 'budget.read', name: 'View Budget', description: 'View budget information' },
      { id: 'budget.update', name: 'Update Budget', description: 'Update budget entries' },
      { id: 'budget.delete', name: 'Delete Budget', description: 'Delete budget entries' }
    ],
    'Report Management': [
      { id: 'report.generate', name: 'Generate Report', description: 'Generate reports' },
      { id: 'report.export', name: 'Export Report', description: 'Export reports to various formats' }
    ]
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => {
      const permissions = prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId];
      return { ...prev, permissions };
    });
    // Clear permission error
    if (errors.permissions) {
      setErrors(prev => ({ ...prev, permissions: '' }));
    }
  };

  const handleSelectAllCategory = (category) => {
    const categoryPermissions = availablePermissions[category].map(p => p.id);
    const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));
    
    setFormData(prev => {
      const permissions = allSelected
        ? prev.permissions.filter(p => !categoryPermissions.includes(p))
        : [...new Set([...prev.permissions, ...categoryPermissions])];
      return { ...prev, permissions };
    });
  };

  const handleSelectAll = () => {
    const allPermissions = Object.values(availablePermissions)
      .flat()
      .map(p => p.id);
    const allSelected = allPermissions.every(p => formData.permissions.includes(p));
    
    setFormData(prev => ({
      ...prev,
      permissions: allSelected ? [] : allPermissions
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Role name must be at least 3 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    // Permissions validation
    if (formData.permissions.length === 0) {
      newErrors.permissions = 'At least one permission must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      console.log('Form submitted:', formData);
      alert(isEditMode ? 'Role updated successfully!' : 'Role created successfully!');
      onBack();
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset the form?')) {
      setFormData({
        name: roleData?.name || '',
        description: roleData?.description || '',
        permissions: roleData?.permissions || []
      });
      setErrors({});
    }
  };

  const getCategorySelectionState = (category) => {
    const categoryPermissions = availablePermissions[category].map(p => p.id);
    const selectedCount = categoryPermissions.filter(p => formData.permissions.includes(p)).length;
    
    if (selectedCount === 0) return 'none';
    if (selectedCount === categoryPermissions.length) return 'all';
    return 'partial';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isEditMode ? 'Edit Role' : 'Add New Role'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Update role information and permissions' : 'Create a new role with permissions'}
            </p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          {/* Form Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
            <h2 className="text-lg font-bold text-gray-800">Role Information</h2>
          </div>

          {/* Form Body */}
          <div className="p-6 space-y-6">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-md font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Basic Information
              </h3>
              <div className="space-y-4">
                {/* Role Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Role Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={isEditMode && roleData?.isSystem}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    } ${isEditMode && roleData?.isSystem ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="e.g., Project Manager"
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                  {isEditMode && roleData?.isSystem && (
                    <p className="text-gray-500 text-xs mt-1">System role name cannot be changed</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe the role and its responsibilities..."
                  />
                  {errors.description && (
                    <p className="text-red-600 text-sm mt-1">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-blue-600" />
                  <h3 className="text-md font-bold text-gray-800">
                    Permissions <span className="text-red-600">*</span>
                  </h3>
                  <span className="text-sm text-gray-500">
                    ({formData.permissions.length} selected)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {Object.values(availablePermissions).flat().length === formData.permissions.length 
                    ? 'Deselect All' 
                    : 'Select All'}
                </button>
              </div>

              {errors.permissions && (
                <p className="text-red-600 text-sm mb-4">{errors.permissions}</p>
              )}

              <div className="space-y-4">
                {Object.entries(availablePermissions).map(([category, permissions]) => {
                  const selectionState = getCategorySelectionState(category);
                  
                  return (
                    <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Category Header */}
                      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-200">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800">{category}</h4>
                          <span className="text-xs text-gray-500">
                            ({permissions.filter(p => formData.permissions.includes(p.id)).length}/{permissions.length})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSelectAllCategory(category)}
                          className={`text-sm font-medium px-3 py-1 rounded ${
                            selectionState === 'all'
                              ? 'bg-blue-100 text-blue-700'
                              : selectionState === 'partial'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {selectionState === 'all' ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      {/* Category Permissions */}
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {permissions.map(permission => (
                            <label
                              key={permission.id}
                              className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                formData.permissions.includes(permission.id)
                                  ? 'bg-blue-50 border-2 border-blue-500'
                                  : 'bg-gray-50 border-2 border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.includes(permission.id)}
                                  onChange={() => handlePermissionToggle(permission.id)}
                                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                />
                                {formData.permissions.includes(permission.id) && (
                                  <Check className="w-4 h-4 text-blue-600 absolute pointer-events-none" style={{ left: '2px', top: '2px' }} />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{permission.name}</div>
                                <div className="text-xs text-gray-500 mt-1">{permission.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Form Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              Reset
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
              >
                <Save className="w-5 h-5" />
                {isEditMode ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleForm;